import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { API, getAuthHeaders } from "../App";
import Layout from "../components/Layout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Settings2, List, Shield, GitBranch, FileText, Plus, Trash2, GripVertical, RotateCcw, Loader2, Save, ChevronDown, ChevronRight, Pencil } from "lucide-react";
import { toast } from "sonner";
import { PageHeader } from "../components/shared/PageHeader";
import { clearDropdownCache } from "../components/shared/DropdownHooks";

const TABS = [
  { id: "dropdowns", label: "Dropdown Values", icon: List, desc: "Statuses, priorities, severities, classifications" },
  { id: "roles", label: "Roles & Permissions", icon: Shield, desc: "Custom roles and permission sets" },
  { id: "workflows", label: "Workflows", icon: GitBranch, desc: "State machines and approval flows" },
  { id: "forms", label: "Custom Fields", icon: FileText, desc: "Add fields to any entity" },
];

const DROPDOWN_LABELS = {
  wo_statuses: "Work Order Statuses",
  wo_priorities: "Work Order Priorities",
  blueprint_classifications: "Blueprint Classifications",
  ncr_severities: "NCR Severities",
  ncr_categories: "NCR Categories",
  maintenance_recurrence: "Maintenance Recurrence",
  document_lifecycle_states: "Document Lifecycle States",
  user_roles: "User Roles",
};

const ENTITY_TYPES = [
  { value: "blueprint", label: "Blueprints" },
  { value: "machine", label: "Machines" },
  { value: "workorder", label: "Work Orders" },
  { value: "maintenance", label: "Maintenance" },
  { value: "ncr", label: "NCRs" },
  { value: "part", label: "Parts" },
];

const FIELD_TYPES = [
  { value: "text", label: "Text" },
  { value: "number", label: "Number" },
  { value: "date", label: "Date" },
  { value: "select", label: "Dropdown" },
  { value: "multiselect", label: "Multi-Select" },
  { value: "checkbox", label: "Checkbox" },
  { value: "textarea", label: "Text Area" },
  { value: "file", label: "File Attachment" },
  { value: "signature", label: "Electronic Signature" },
];

export default function CustomizationPage({ onLogout, userRole, isFeatureEnabled }) {
  const [tab, setTab] = useState("dropdowns");
  const [loading, setLoading] = useState(true);
  // Dropdowns
  const [dropdownKeys, setDropdownKeys] = useState([]);
  const [selectedDropdown, setSelectedDropdown] = useState(null);
  const [dropdownValues, setDropdownValues] = useState([]);
  const [dropdownDirty, setDropdownDirty] = useState(false);
  // Roles
  const [roles, setRoles] = useState({ system_roles: [], custom_roles: [] });
  const [permissions, setPermissions] = useState([]);
  const [editingRole, setEditingRole] = useState(null);
  const [rolePerms, setRolePerms] = useState([]);
  const [addRoleOpen, setAddRoleOpen] = useState(false);
  const [newRole, setNewRole] = useState({ key: "", label: "" });
  // Workflows
  const [workflows, setWorkflows] = useState([]);
  const [editingWF, setEditingWF] = useState(null);
  const [wfForm, setWfForm] = useState({ name: "", entity_type: "", description: "", states: [], transitions: [] });
  // Forms
  const [formSchemas, setFormSchemas] = useState([]);
  const [selectedEntity, setSelectedEntity] = useState(null);
  const [entityFields, setEntityFields] = useState([]);
  const [saving, setSaving] = useState(false);

  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      const [dkResp, rolesResp, permsResp, wfResp, formsResp] = await Promise.allSettled([
        axios.get(`${API}/customization/dropdowns`, { headers: getAuthHeaders() }),
        axios.get(`${API}/customization/roles`, { headers: getAuthHeaders() }),
        axios.get(`${API}/customization/permissions`, { headers: getAuthHeaders() }),
        axios.get(`${API}/customization/workflows`, { headers: getAuthHeaders() }),
        axios.get(`${API}/customization/forms`, { headers: getAuthHeaders() }),
      ]);
      if (dkResp.status === "fulfilled") setDropdownKeys(dkResp.value.data.all_keys || []);
      if (rolesResp.status === "fulfilled") setRoles(rolesResp.value.data);
      if (permsResp.status === "fulfilled") setPermissions(permsResp.value.data.permissions || []);
      if (wfResp.status === "fulfilled") setWorkflows(wfResp.value.data || []);
      if (formsResp.status === "fulfilled") setFormSchemas(formsResp.value.data || []);
    } catch { /* silent */ }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  // Dropdown handlers
  const loadDropdown = async (key) => {
    setSelectedDropdown(key);
    try {
      const resp = await axios.get(`${API}/customization/dropdowns/${key}`, { headers: getAuthHeaders() });
      setDropdownValues(resp.data.values || []);
      setDropdownDirty(false);
    } catch { toast.error("Failed to load dropdown"); }
  };

  const saveDropdown = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/customization/dropdowns/${selectedDropdown}`, { values: dropdownValues }, { headers: getAuthHeaders() });
      toast.success("Dropdown saved");
      setDropdownDirty(false);
      clearDropdownCache(selectedDropdown);
    } catch (err) { toast.error(err.response?.data?.detail || "Save failed"); }
    finally { setSaving(false); }
  };

  const resetDropdown = async () => {
    if (!window.confirm("Reset to system defaults?")) return;
    try {
      const resp = await axios.delete(`${API}/customization/dropdowns/${selectedDropdown}`, { headers: getAuthHeaders() });
      setDropdownValues(resp.data.values || []);
      setDropdownDirty(false);
      toast.success("Reset to defaults");
    } catch { toast.error("Reset failed"); }
  };

  const addDropdownValue = () => {
    setDropdownValues(prev => [...prev, { value: "", label: "", color: "#64748B", order: prev.length }]);
    setDropdownDirty(true);
  };

  const updateDV = (i, field, val) => {
    setDropdownValues(prev => prev.map((v, j) => j === i ? { ...v, [field]: val } : v));
    setDropdownDirty(true);
  };

  const removeDV = (i) => {
    setDropdownValues(prev => prev.filter((_, j) => j !== i));
    setDropdownDirty(true);
  };

  // Role handlers
  const openRoleEdit = (role) => {
    setEditingRole(role);
    setRolePerms([...(role.permissions || [])]);
  };

  const togglePerm = (key) => {
    setRolePerms(prev => prev.includes(key) ? prev.filter(k => k !== key) : [...prev, key]);
  };

  const saveRolePerms = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/customization/roles/${editingRole.key}`, { permissions: rolePerms }, { headers: getAuthHeaders() });
      toast.success("Role permissions saved");
      setEditingRole(null);
      fetchAll();
    } catch (err) { toast.error(err.response?.data?.detail || "Save failed"); }
    finally { setSaving(false); }
  };

  const createRole = async () => {
    if (!newRole.key || !newRole.label) { toast.error("Key and label required"); return; }
    try {
      await axios.post(`${API}/customization/roles`, { ...newRole, permissions: [] }, { headers: getAuthHeaders() });
      toast.success("Role created");
      setAddRoleOpen(false);
      setNewRole({ key: "", label: "" });
      fetchAll();
    } catch (err) { toast.error(err.response?.data?.detail || "Failed"); }
  };

  const deleteRole = async (key) => {
    if (!window.confirm(`Delete role "${key}"?`)) return;
    try {
      await axios.delete(`${API}/customization/roles/${key}`, { headers: getAuthHeaders() });
      toast.success("Role deleted");
      fetchAll();
    } catch { toast.error("Failed"); }
  };

  // Workflow handlers
  const createWorkflow = async () => {
    if (!wfForm.name || !wfForm.entity_type) { toast.error("Name and entity type required"); return; }
    try {
      await axios.post(`${API}/customization/workflows`, wfForm, { headers: getAuthHeaders() });
      toast.success("Workflow created");
      setEditingWF(null);
      setWfForm({ name: "", entity_type: "", description: "", states: [], transitions: [] });
      fetchAll();
    } catch (err) { toast.error(err.response?.data?.detail || "Failed"); }
  };

  const deleteWorkflow = async (id) => {
    if (!window.confirm("Delete this workflow?")) return;
    try {
      await axios.delete(`${API}/customization/workflows/${id}`, { headers: getAuthHeaders() });
      toast.success("Deleted");
      fetchAll();
    } catch { toast.error("Failed"); }
  };

  // Form handlers
  const loadEntityFields = async (entityType) => {
    setSelectedEntity(entityType);
    try {
      const resp = await axios.get(`${API}/customization/forms/${entityType}`, { headers: getAuthHeaders() });
      setEntityFields(resp.data.fields || []);
    } catch { setEntityFields([]); }
  };

  const saveEntityFields = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/customization/forms/${selectedEntity}`, { fields: entityFields }, { headers: getAuthHeaders() });
      toast.success("Custom fields saved");
    } catch (err) { toast.error(err.response?.data?.detail || "Save failed"); }
    finally { setSaving(false); }
  };

  const addField = () => {
    setEntityFields(prev => [...prev, { key: "", label: "", type: "text", required: false, options: [] }]);
  };

  const updateField = (i, k, v) => {
    setEntityFields(prev => prev.map((f, j) => j === i ? { ...f, [k]: v } : f));
  };

  const removeField = (i) => {
    setEntityFields(prev => prev.filter((_, j) => j !== i));
  };

  if (loading) return <Layout onLogout={onLogout} userRole={userRole} isFeatureEnabled={isFeatureEnabled}><div className="flex items-center justify-center h-64"><Loader2 className="animate-spin text-[#64748B]" size={24} /></div></Layout>;

  const permsByCategory = permissions.reduce((acc, p) => { (acc[p.category] = acc[p.category] || []).push(p); return acc; }, {});

  return (
    <Layout onLogout={onLogout} userRole={userRole} isFeatureEnabled={isFeatureEnabled}>
      <div className="space-y-6" data-testid="customization-page">
        <PageHeader title="Platform Customization" subtitle="Configure dropdowns, roles, workflows, and custom fields — no code required" testId="customization-title" />

        {/* Tabs */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          {TABS.map(t => (
            <button key={t.id} onClick={() => setTab(t.id)} className={`p-4 border text-left transition-colors ${tab === t.id ? "border-[#2563EB] bg-[#EFF6FF]" : "border-[#E2E8F0] bg-white hover:border-[#94A3B8]"}`} data-testid={`tab-${t.id}`}>
              <div className="flex items-center gap-2 mb-1">
                <t.icon size={16} className={tab === t.id ? "text-[#2563EB]" : "text-[#64748B]"} />
                <span className="text-sm font-bold text-[#0F172A]">{t.label}</span>
              </div>
              <p className="text-[10px] text-[#94A3B8]">{t.desc}</p>
            </button>
          ))}
        </div>

        {/* ─── DROPDOWNS TAB ─── */}
        {tab === "dropdowns" && (
          <div className="flex gap-4" data-testid="dropdowns-panel">
            <div className="w-64 bg-white border border-[#E2E8F0] shrink-0">
              <div className="p-3 border-b border-[#E2E8F0] bg-[#FAFAFA]"><span className="text-xs font-bold text-[#0F172A] uppercase tracking-wider">Categories</span></div>
              {dropdownKeys.map(key => (
                <button key={key} onClick={() => loadDropdown(key)} className={`w-full text-left px-3 py-2.5 text-sm border-b border-[#E2E8F0] transition-colors ${selectedDropdown === key ? "bg-[#EFF6FF] text-[#2563EB] font-medium" : "text-[#0F172A] hover:bg-[#FAFAFA]"}`} data-testid={`dropdown-${key}`}>
                  {DROPDOWN_LABELS[key] || key}
                </button>
              ))}
            </div>
            <div className="flex-1 bg-white border border-[#E2E8F0]">
              {!selectedDropdown ? (
                <div className="p-8 text-center text-sm text-[#94A3B8]">Select a category to edit its values</div>
              ) : (
                <div>
                  <div className="p-4 border-b border-[#E2E8F0] flex items-center justify-between">
                    <h3 className="font-bold text-sm text-[#0F172A]">{DROPDOWN_LABELS[selectedDropdown] || selectedDropdown}</h3>
                    <div className="flex gap-2">
                      <Button variant="outline" size="sm" onClick={resetDropdown} className="rounded-none text-xs" data-testid="dropdown-reset"><RotateCcw size={12} className="mr-1" /> Reset</Button>
                      <Button size="sm" onClick={addDropdownValue} className="rounded-none bg-[#D97706] text-white text-xs" data-testid="dropdown-add"><Plus size={12} className="mr-1" /> Add Value</Button>
                      <Button size="sm" onClick={saveDropdown} disabled={saving || !dropdownDirty} className="rounded-none bg-[#2563EB] text-white text-xs" data-testid="dropdown-save"><Save size={12} className="mr-1" /> Save</Button>
                    </div>
                  </div>
                  <div className="divide-y divide-[#E2E8F0]">
                    {dropdownValues.map((v, i) => (
                      <div key={i} className="px-4 py-2 flex items-center gap-3" data-testid={`dv-${i}`}>
                        <GripVertical size={14} className="text-[#CBD5E1]" />
                        <input type="color" value={v.color || "#64748B"} onChange={e => updateDV(i, "color", e.target.value)} className="w-6 h-6 border-0 cursor-pointer" />
                        <Input value={v.value} onChange={e => updateDV(i, "value", e.target.value)} placeholder="value_key" className="w-36 rounded-none font-mono text-xs h-8" />
                        <Input value={v.label} onChange={e => updateDV(i, "label", e.target.value)} placeholder="Display Label" className="flex-1 rounded-none text-xs h-8" />
                        <button onClick={() => removeDV(i)} className="text-[#DC2626] hover:bg-[#FEF2F2] p-1"><Trash2 size={14} /></button>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        )}

        {/* ─── ROLES TAB ─── */}
        {tab === "roles" && (
          <div className="bg-white border border-[#E2E8F0]" data-testid="roles-panel">
            <div className="p-4 border-b border-[#E2E8F0] flex items-center justify-between">
              <h3 className="font-bold text-sm text-[#0F172A]">Roles & Permissions</h3>
              <Button size="sm" onClick={() => setAddRoleOpen(true)} className="rounded-none bg-[#D97706] text-white text-xs" data-testid="add-role-btn"><Plus size={12} className="mr-1" /> New Role</Button>
            </div>
            <div className="divide-y divide-[#E2E8F0]">
              {[...roles.system_roles, ...roles.custom_roles].map(role => (
                <div key={role.key} className="px-4 py-3 flex items-center justify-between hover:bg-[#FAFAFA]" data-testid={`role-${role.key}`}>
                  <div>
                    <div className="flex items-center gap-2">
                      <span className="font-bold text-sm text-[#0F172A]">{role.label}</span>
                      {role.is_system && <span className="text-[9px] px-1.5 py-0.5 bg-[#F8FAFC] border border-[#E2E8F0] text-[#64748B] font-bold uppercase">System</span>}
                      {role.is_overridden && <span className="text-[9px] px-1.5 py-0.5 bg-[#FEF3C7] border border-[#F59E0B] text-[#92400E] font-bold uppercase">Customized</span>}
                    </div>
                    <p className="text-xs text-[#64748B]">{(role.permissions || []).length} permissions</p>
                  </div>
                  <div className="flex gap-1">
                    <Button size="sm" variant="outline" onClick={() => openRoleEdit(role)} className="rounded-none" data-testid={`edit-role-${role.key}`}><Pencil size={14} /></Button>
                    {!role.is_system && <Button size="sm" variant="outline" onClick={() => deleteRole(role.key)} className="rounded-none border-[#DC2626] text-[#DC2626]"><Trash2 size={14} /></Button>}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* ─── WORKFLOWS TAB ─── */}
        {tab === "workflows" && (
          <div className="bg-white border border-[#E2E8F0]" data-testid="workflows-panel">
            <div className="p-4 border-b border-[#E2E8F0] flex items-center justify-between">
              <h3 className="font-bold text-sm text-[#0F172A]">Workflow Definitions</h3>
              <Button size="sm" onClick={() => setEditingWF("new")} className="rounded-none bg-[#D97706] text-white text-xs" data-testid="add-workflow-btn"><Plus size={12} className="mr-1" /> New Workflow</Button>
            </div>
            {workflows.length === 0 ? (
              <div className="p-8 text-center text-sm text-[#94A3B8]"><GitBranch size={32} className="mx-auto mb-2 text-[#CBD5E1]" /> No custom workflows defined. Create one to define state machines for your processes.</div>
            ) : (
              <div className="divide-y divide-[#E2E8F0]">
                {workflows.map(wf => (
                  <div key={wf.id} className="px-4 py-3 flex items-center justify-between hover:bg-[#FAFAFA]" data-testid={`wf-${wf.id}`}>
                    <div>
                      <div className="flex items-center gap-2">
                        <span className="font-bold text-sm text-[#0F172A]">{wf.name}</span>
                        <span className="text-[9px] px-1.5 py-0.5 bg-[#EFF6FF] border border-[#2563EB] text-[#2563EB] font-bold uppercase">{wf.entity_type}</span>
                        <span className="text-[9px] text-[#94A3B8]">v{wf.version}</span>
                      </div>
                      <p className="text-xs text-[#64748B]">{(wf.states || []).length} states, {(wf.transitions || []).length} transitions</p>
                    </div>
                    <Button size="sm" variant="outline" onClick={() => deleteWorkflow(wf.id)} className="rounded-none border-[#DC2626] text-[#DC2626]"><Trash2 size={14} /></Button>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* ─── CUSTOM FIELDS TAB ─── */}
        {tab === "forms" && (
          <div className="flex gap-4" data-testid="forms-panel">
            <div className="w-48 bg-white border border-[#E2E8F0] shrink-0">
              <div className="p-3 border-b border-[#E2E8F0] bg-[#FAFAFA]"><span className="text-xs font-bold text-[#0F172A] uppercase tracking-wider">Entities</span></div>
              {ENTITY_TYPES.map(et => (
                <button key={et.value} onClick={() => loadEntityFields(et.value)} className={`w-full text-left px-3 py-2.5 text-sm border-b border-[#E2E8F0] ${selectedEntity === et.value ? "bg-[#EFF6FF] text-[#2563EB] font-medium" : "text-[#0F172A] hover:bg-[#FAFAFA]"}`} data-testid={`entity-${et.value}`}>
                  {et.label}
                  {formSchemas.find(s => s.entity_type === et.value) && <span className="ml-1 text-[9px] text-[#10B981]">*</span>}
                </button>
              ))}
            </div>
            <div className="flex-1 bg-white border border-[#E2E8F0]">
              {!selectedEntity ? (
                <div className="p-8 text-center text-sm text-[#94A3B8]">Select an entity to manage its custom fields</div>
              ) : (
                <div>
                  <div className="p-4 border-b border-[#E2E8F0] flex items-center justify-between">
                    <h3 className="font-bold text-sm text-[#0F172A]">{ENTITY_TYPES.find(e => e.value === selectedEntity)?.label} — Custom Fields</h3>
                    <div className="flex gap-2">
                      <Button size="sm" onClick={addField} className="rounded-none bg-[#D97706] text-white text-xs" data-testid="add-field-btn"><Plus size={12} className="mr-1" /> Add Field</Button>
                      <Button size="sm" onClick={saveEntityFields} disabled={saving} className="rounded-none bg-[#2563EB] text-white text-xs" data-testid="save-fields-btn"><Save size={12} className="mr-1" /> Save</Button>
                    </div>
                  </div>
                  {entityFields.length === 0 ? (
                    <div className="p-6 text-center text-sm text-[#94A3B8]">No custom fields. Click "Add Field" to extend this entity.</div>
                  ) : (
                    <div className="divide-y divide-[#E2E8F0]">
                      {entityFields.map((f, i) => (
                        <div key={i} className="px-4 py-2 flex items-center gap-3" data-testid={`field-${i}`}>
                          <Input value={f.key} onChange={e => updateField(i, "key", e.target.value)} placeholder="field_key" className="w-32 rounded-none font-mono text-xs h-8" />
                          <Input value={f.label} onChange={e => updateField(i, "label", e.target.value)} placeholder="Display Label" className="flex-1 rounded-none text-xs h-8" />
                          <select value={f.type} onChange={e => updateField(i, "type", e.target.value)} className="border border-[#E2E8F0] px-2 py-1 text-xs w-28">
                            {FIELD_TYPES.map(ft => <option key={ft.value} value={ft.value}>{ft.label}</option>)}
                          </select>
                          <label className="flex items-center gap-1 text-xs text-[#64748B]">
                            <input type="checkbox" checked={f.required || false} onChange={e => updateField(i, "required", e.target.checked)} /> Req
                          </label>
                          <button onClick={() => removeField(i)} className="text-[#DC2626] hover:bg-[#FEF2F2] p-1"><Trash2 size={14} /></button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Role Edit Dialog */}
      <Dialog open={!!editingRole} onOpenChange={(o) => { if (!o) setEditingRole(null); }}>
        <DialogContent className="bg-white rounded-none border border-[#E2E8F0] max-w-lg max-h-[80vh] overflow-y-auto" data-testid="role-edit-dialog">
          <DialogHeader>
            <DialogTitle className="text-[#0F172A]">Edit Permissions — {editingRole?.label}</DialogTitle>
            <DialogDescription className="text-xs text-[#64748B]">Toggle permissions for this role. Changes apply to all users with this role.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            {Object.entries(permsByCategory).map(([cat, perms]) => (
              <div key={cat}>
                <h4 className="text-xs font-bold text-[#0F172A] uppercase tracking-wider mb-1">{cat}</h4>
                <div className="space-y-1">
                  {perms.map(p => (
                    <button key={p.key} onClick={() => togglePerm(p.key)} className={`w-full text-left px-3 py-2 text-sm border transition-colors ${rolePerms.includes(p.key) ? "border-[#10B981] bg-[#F0FDF4]" : "border-[#E2E8F0] bg-white text-[#94A3B8]"}`}>
                      {p.label}
                    </button>
                  ))}
                </div>
              </div>
            ))}
            <Button onClick={saveRolePerms} disabled={saving} className="w-full bg-[#2563EB] text-white rounded-none" data-testid="save-role-btn">{saving ? "Saving..." : "Save Permissions"}</Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Add Role Dialog */}
      <Dialog open={addRoleOpen} onOpenChange={setAddRoleOpen}>
        <DialogContent className="bg-white rounded-none border border-[#E2E8F0] max-w-sm" data-testid="add-role-dialog">
          <DialogHeader>
            <DialogTitle className="text-[#0F172A]">Create Custom Role</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div><Label className="text-xs font-bold uppercase">Key (no spaces)</Label><Input value={newRole.key} onChange={e => setNewRole(r => ({ ...r, key: e.target.value.toLowerCase().replace(/\s/g, "_") }))} className="mt-1 rounded-none font-mono text-xs" placeholder="quality_inspector" data-testid="new-role-key" /></div>
            <div><Label className="text-xs font-bold uppercase">Display Name</Label><Input value={newRole.label} onChange={e => setNewRole(r => ({ ...r, label: e.target.value }))} className="mt-1 rounded-none text-xs" placeholder="Quality Inspector" data-testid="new-role-label" /></div>
            <Button onClick={createRole} className="w-full bg-[#10B981] text-white rounded-none" data-testid="create-role-submit">Create Role</Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Create Workflow Dialog */}
      <Dialog open={editingWF === "new"} onOpenChange={(o) => { if (!o) setEditingWF(null); }}>
        <DialogContent className="bg-white rounded-none border border-[#E2E8F0] max-w-md" data-testid="create-workflow-dialog">
          <DialogHeader>
            <DialogTitle className="text-[#0F172A]">Create Workflow</DialogTitle>
            <DialogDescription className="text-xs text-[#64748B]">Define a state machine for an entity type.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div><Label className="text-xs font-bold uppercase">Name</Label><Input value={wfForm.name} onChange={e => setWfForm(f => ({ ...f, name: e.target.value }))} className="mt-1 rounded-none text-xs" placeholder="Work Order Lifecycle" data-testid="wf-name" /></div>
            <div>
              <Label className="text-xs font-bold uppercase">Entity Type</Label>
              <select value={wfForm.entity_type} onChange={e => setWfForm(f => ({ ...f, entity_type: e.target.value }))} className="mt-1 w-full border border-[#E2E8F0] p-2 text-sm" data-testid="wf-entity">
                <option value="">Select...</option>
                {ENTITY_TYPES.map(et => <option key={et.value} value={et.value}>{et.label}</option>)}
                <option value="ecr">ECR</option>
                <option value="eco">ECO</option>
                <option value="procedure">Procedure</option>
                <option value="audit">Audit</option>
                <option value="scar">SCAR</option>
              </select>
            </div>
            <div><Label className="text-xs font-bold uppercase">Description</Label><Input value={wfForm.description} onChange={e => setWfForm(f => ({ ...f, description: e.target.value }))} className="mt-1 rounded-none text-xs" data-testid="wf-desc" /></div>
            <Button onClick={createWorkflow} className="w-full bg-[#10B981] text-white rounded-none" data-testid="wf-create-submit">Create Workflow</Button>
          </div>
        </DialogContent>
      </Dialog>
    </Layout>
  );
}
